/**
 * Decide whether a glossary term requires an exact-case match in content.
 *
 * The rule is purely casing-driven: a term that contains no lowercase
 * letters AND at least one uppercase letter is treated as an all-caps
 * term and requires an exact-case match. Any term with a lowercase
 * letter (including the common mixed-case entries like "iPhone" or
 * "Alexa") matches case-insensitively.
 *
 * Rationale: when authors write a glossary entry in all caps they are
 * declaring an acronym and the lowercase surface form is ambiguous with
 * ordinary prose — "from the kitchen" should not highlight as the
 * `FROM` acronym. Mixed-case entries carry their own disambiguation in
 * the surface form.
 *
 * Single-character terms always return false: there is not enough signal
 * to disambiguate "a" from the article "a".
 *
 * The historical `COMMON_ENGLISH_STOPWORDS` set used to gate this
 * decision; the all-caps regex supersedes it, so the set is gone. If a
 * mixed-case glossary entry ever needs case-sensitive matching, extend
 * this function with an explicit opt-in rather than reintroducing the
 * stopwords list.
 */
export function requiresExactCaseMatch(canonicalTerm: string): boolean {
    if (canonicalTerm.length < 2) {
        return false;
    }
    // No lowercase letters at all AND at least one uppercase letter.
    return !/[a-z]/.test(canonicalTerm) && /[A-Z]/.test(canonicalTerm);
}
